// @tennis-matches/services/masterdata/postal-address
// Strukturierte Postadressen für Geschäftspartner (Trainer, Spieler, Rechnungsempfänger).

export const POSTAL_ADDRESS_FIELD_KEYS = Object.freeze([
  "street",
  "house_number",
  "postal_code",
  "city",
  "country",
]);

function __nz(v) {
  return String(v ?? "").trim();
}

/** @typedef {{ ok: true } | { ok: false, i18nKey: string }} PostalAddressFieldValidation */

const HOUSE_NUMBER_SIMPLE_RE = /^\d{1,4}[a-zA-Z]{0,2}$/;
const HOUSE_NUMBER_RANGE_RE =
  /^\d{1,4}[a-zA-Z]{0,2}\s*-\s*\d{1,4}[a-zA-Z]{0,2}$/;

/**
 * Hausnummer (D-A-CH): 1–4 Ziffern, optional bis zu 2 Buchstaben (z. B. 12, 12a, 12ab).
 * Bereiche wie „12-14“ oder „12a-14b“ sind erlaubt.
 * @param {string} value
 * @returns {PostalAddressFieldValidation}
 */
export function validateHouseNumber(value) {
  const v = __nz(value);
  if (!v) return { ok: true };
  if (HOUSE_NUMBER_SIMPLE_RE.test(v) || HOUSE_NUMBER_RANGE_RE.test(v)) {
    return { ok: true };
  }
  return {
    ok: false,
    i18nKey: "masterdata.postalAddress.validation.houseNumber",
  };
}

/**
 * Ortsname (D-A-CH): keine Ziffern — verhindert PLZ/Telefon im Ortsfeld.
 * @param {string} value
 * @returns {PostalAddressFieldValidation}
 */
export function validateCityName(value) {
  const v = __nz(value);
  if (!v) return { ok: true };
  if (/\d/.test(v)) {
    return {
      ok: false,
      i18nKey: "masterdata.postalAddress.validation.cityNoDigits",
    };
  }
  return { ok: true };
}

/**
 * PLZ (D-A-CH): DE = 5 Ziffern, AT/CH = 4 Ziffern.
 * @param {string} value
 * @param {{ country?: string }} [opts]
 * @returns {PostalAddressFieldValidation}
 */
export function validatePostalCode(value, { country = "DE" } = {}) {
  const v = __nz(value);
  if (!v) return { ok: true };
  const c = __nz(country).toUpperCase() || "DE";
  if (c === "AT" || c === "CH") {
    if (/^\d{4}$/.test(v)) return { ok: true };
    return {
      ok: false,
      i18nKey: "masterdata.postalAddress.validation.postalCodeAtCh",
    };
  }
  if (/^\d{5}$/.test(v)) return { ok: true };
  return {
    ok: false,
    i18nKey: "masterdata.postalAddress.validation.postalCodeDe",
  };
}

/**
 * @param {"house_number"|"city"|"postal_code"} fieldKey
 * @param {string} value
 * @param {{ country?: string }} [opts]
 * @returns {PostalAddressFieldValidation}
 */
export function validatePostalAddressField(fieldKey, value, opts = {}) {
  if (fieldKey === "house_number") return validateHouseNumber(value);
  if (fieldKey === "city") return validateCityName(value);
  if (fieldKey === "postal_code") return validatePostalCode(value, opts);
  return { ok: true };
}

/**
 * @param {Record<string, unknown>|null|undefined} values
 * @returns {PostalAddressFieldValidation}
 */
export function validatePostalAddressFormValues(values) {
  const v = values && typeof values === "object" ? values : {};
  const country = __nz(v.country) || "DE";
  const checks = [
    validateHouseNumber(__nz(v.house_number)),
    validatePostalCode(__nz(v.postal_code), { country }),
    validateCityName(__nz(v.city)),
  ];
  for (const result of checks) {
    if (!result.ok) return result;
  }
  return { ok: true };
}

/**
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {Record<string, string|null>}
 */
export function pickPostalAddressFields(row) {
  const src = row && typeof row === "object" ? row : {};
  const country = __nz(src.country) || "DE";
  const street = __nz(src.street);
  const house_number = __nz(src.house_number);
  const postal_code = __nz(src.postal_code);
  const city = __nz(src.city);

  if (!street && !postal_code && !city) {
    const legacy = __nz(src.address);
    if (legacy) {
      return {
        street: legacy,
        house_number: null,
        postal_code: null,
        city: null,
        country: country || "DE",
      };
    }
  }

  return {
    street: street || null,
    house_number: house_number || null,
    postal_code: postal_code || null,
    city: city || null,
    country: country || "DE",
  };
}

/**
 * Formularwerte aus DB-Zeile (leere Strings statt null).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {Record<string, string>}
 */
export function postalAddressFormValuesFromRow(row) {
  const f = pickPostalAddressFields(row);
  return {
    street: f.street || "",
    house_number: f.house_number || "",
    postal_code: f.postal_code || "",
    city: f.city || "",
    country: f.country || "DE",
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {boolean}
 */
export function hasPostalAddress(row) {
  const f = pickPostalAddressFields(row);
  return Boolean(f.street || f.postal_code || f.city);
}

/**
 * Teilt kombinierte Einzeilen-Adressen (Legacy/Snapshot) in Straße und PLZ/Ort.
 * @param {string} text
 * @returns {string[]}
 */
export function splitCombinedPostalAddressLine(text) {
  const raw = __nz(text);
  if (!raw) return [];
  const match = /^(.+?),\s*(\d{4,5}\s+.+)$/.exec(raw);
  if (match) return [__nz(match[1]), __nz(match[2])].filter(Boolean);
  return [raw];
}

/**
 * Mehrzeilige Anzeige für Rechnungen (Straße · PLZ Ort).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {string[]}
 */
export function formatPostalAddressLines(row) {
  const f = pickPostalAddressFields(row);
  const streetLine = [f.street, f.house_number].filter(Boolean).join(" ");
  const cityLine = [f.postal_code, f.city].filter(Boolean).join(" ");
  const lines = [streetLine, cityLine].filter(Boolean);
  const c = __nz(f.country);
  if (c && c !== "DE") lines.push(c);

  if (lines.length === 1) {
    const split = splitCombinedPostalAddressLine(lines[0]);
    if (split.length > 1) return split;
  }
  return lines;
}

/**
 * Snapshot- oder Freitext-Adresse in Rechnungszeilen (Straße · PLZ Ort).
 * @param {string} snapshot
 * @returns {string[]}
 */
export function formatPostalAddressSnapshotLines(snapshot) {
  return splitCombinedPostalAddressLine(__nz(snapshot));
}

/**
 * Einzeilige Anzeige (Karten, Meta-Zeilen).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {string}
 */
export function formatPostalAddressLine(row) {
  return formatPostalAddressLines(row).join(", ");
}

/**
 * Legacy-Feld `address` aus strukturierten Feldern (Abwärtskompatibilität).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {string|null}
 */
export function buildLegacyAddressFromPostalFields(row) {
  const line = formatPostalAddressLine(row);
  return line || null;
}

/**
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {Record<string, string|null>}
 */
export function buildPostalAddressPersistPayload(row) {
  return pickPostalAddressFields(row);
}

/**
 * Leert Adressfelder (Anonymisierung).
 * @returns {Record<string, null>}
 */
export function emptyPostalAddressFields() {
  return {
    street: null,
    house_number: null,
    postal_code: null,
    city: null,
    country: null,
  };
}
